/**
 * Pure decision logic for the deployment refresh framework.
 *
 * Extracted from the provider so the safety properties can be tested directly —
 * in particular the two that matter operationally:
 *
 *   1. A client on the current build is never prompted and never reloaded, no
 *      matter how often the user logs in or navigates.
 *   2. A mandatory release force-refreshes a given device at most once, so a
 *      client that still reports stale afterwards degrades to a dismissable
 *      prompt instead of reloading forever.
 */

/**
 * Build ids that do not identify a deployment: local dev, or a build where
 * COMMIT_HASH was never injected. Drift detection stays off for these, otherwise
 * every dev server would prompt against whatever is currently deployed.
 */
export const NON_DEPLOY_BUILD_IDS: ReadonlySet<string> = new Set([
  '',
  'dev',
  'unknown',
  '0.0.0',
])

export interface UpdatePolicyInput {
  /** Build id baked into the running bundle (COMMIT_HASH). */
  localBuildId: string
  /** Build id reported by /build-version.json, or null if not fetched yet. */
  remoteBuildId: string | null
  /** Whether the deployed release is flagged mandatory. */
  remoteMandatory: boolean
  /** Build id the user chose to postpone, from sessionStorage. */
  dismissedBuildId: string | null
  /** Build id we have already force-refreshed for, from localStorage. */
  autoRefreshedBuildId: string | null
}

export interface UpdatePolicyResult {
  /** Whether drift detection should run at all for this bundle. */
  enabled: boolean
  /** Whether to show the refresh prompt. */
  updateAvailable: boolean
  /** Whether to block and refresh automatically rather than ask. */
  mandatory: boolean
}

/** True when this bundle came from a real deployment. */
export function isDeploymentBuildId(buildId: string): boolean {
  return !NON_DEPLOY_BUILD_IDS.has(buildId.trim())
}

export function evaluateUpdatePolicy(input: UpdatePolicyInput): UpdatePolicyResult {
  const localBuildId = input.localBuildId.trim()
  const enabled = isDeploymentBuildId(localBuildId)
  const off: UpdatePolicyResult = { enabled, updateAvailable: false, mandatory: false }

  if (!enabled) return off
  if (!input.remoteBuildId) return off

  const remoteBuildId = input.remoteBuildId.trim()

  // The only staleness signal. Equal ids mean the client is current: no prompt,
  // no reload, regardless of session, login state or elapsed time.
  if (remoteBuildId === localBuildId) return off

  // Postponed for this tab session.
  if (input.dismissedBuildId === remoteBuildId) return off

  // Loop guard: one automatic refresh per build id per device. If we already
  // refreshed for this build and the client still reads as stale, refreshing
  // again cannot fix it, so fall back to asking.
  const alreadyForced = input.autoRefreshedBuildId === remoteBuildId
  const mandatory = input.remoteMandatory && !alreadyForced

  return { enabled, updateAvailable: true, mandatory }
}
